import { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import i18next from 'i18next';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { PromQuestionDto } from '../../../../shared/dtos/prom.dto';
import PrimaryButton from '../PrimaryButton';
import PromQuestionModal from './PromQuestionModal';
import PromQuestionTable from './PromQuestionTable';

const PromQuestionManagement: FC = (): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [promQuestions, setPromQuestions] = useState<PromQuestionDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editPromQuestion, setEditPromQuestion] = useState<PromQuestionDto | null>(null);

  const fetchPromQuestions: () => Promise<void> = useCallback(async () => {
    setIsLoading(true);

    try {
      const result: PromQuestionDto[] = await authFetch('/api/v1/promquestions', { method: 'GET' });
      setPromQuestions(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    void fetchPromQuestions();
  }, [fetchPromQuestions]);

  const closeModal = (): void => {
    setShowModal(false);
    setEditPromQuestion(null);
  };

  const openCreateModal = (): void => {
    setEditPromQuestion(null);
    setShowModal(true);
  };

  const openEditModal = (promQuestion: PromQuestionDto): void => {
    setEditPromQuestion(promQuestion);
    setShowModal(true);
  };

  return (
    <>
      <PromQuestionModal
        show={showModal}
        promQuestion={editPromQuestion ?? undefined}
        onClose={closeModal}
        onSave={() => fetchPromQuestions()}
      />

      <div className="pt-3">
        <PromQuestionTable
          promQuestions={promQuestions}
          isLoading={isLoading}
          toolbar={(
            <PrimaryButton className="ms-auto" onClick={openCreateModal} disabled={isLoading}>
              {i18next.t('generic.create')}
            </PrimaryButton>
          )}
          onClick={openEditModal}
        />
      </div>
    </>
  );
};

export default PromQuestionManagement;
